import React, { createContext, useContext, memo, useMemo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { EmptyState } from "@toddle-edu/ds-web";
import { NoAccessIllustration } from "@toddle-edu/ds-theme";
import { useTranslation } from "react-i18next";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import ACLStorage from "lib/aclService";
import classes from "./TranscriptsPermissionContext.scss";
import {
  TRANSCRIPT_FOR_ACCOUNT_LEVEL_VIEW_PERMISSION,
  TRANSCRIPT_CAN_MANAGE_TEMPLATES_PERMISSION,
  TRANSCRIPT_FOR_PROGRAMME_LEVEL_VIEW_PERMISSION,
  TRANSCRIPT_FOR_PROGRAMME_CAN_MANAGE_TEMPLATES_PERMISSION,
  TRANSCRIPT_CAN_MANAGE_ACADEMIC_RECORDS_PERMISSION,
  TRANSCRIPT_CAN_MANAGE_TRANSCRIPT_DOWNLOAD_PERMISSION,
} from "Constants/permissionConstants";

const TranscriptsPermissionContext = createContext(null);

const addModuleSettingIfAllowed = ({ permissions, context, name }) => {
  if (ACLStorage.getModuleSettingsValueByContext(name, context)) {
    permissions.add(name);
  }
};

const addPermissionIfAllowed = ({ permissions, context, name }) => {
  if (ACLStorage.getPermissionValueByContext(name, context)) {
    permissions.add(name);
  }
};

const getAccountPermissions = () => {
  const permissions = new Set();
  const context = [];

  const moduleSettings = [TRANSCRIPT_FOR_ACCOUNT_LEVEL_VIEW_PERMISSION];
  const permissionNames = [TRANSCRIPT_CAN_MANAGE_TEMPLATES_PERMISSION];

  moduleSettings.forEach(name =>
    addModuleSettingIfAllowed({ permissions, context, name })
  );
  permissionNames.forEach(name =>
    addPermissionIfAllowed({ permissions, context, name })
  );

  return permissions;
};

const getCurriculumPermissions = curriculumId => {
  const permissions = new Set();

  const contexts = generateRnPContextsByLevel({
    level: "CURRICULUM_PROGRAM",
    curriculumProgramId: curriculumId,
  });

  const context = _.get(contexts, "[0]", []);

  const moduleSettings = [TRANSCRIPT_FOR_PROGRAMME_LEVEL_VIEW_PERMISSION];
  const permissionNames = [
    TRANSCRIPT_FOR_PROGRAMME_CAN_MANAGE_TEMPLATES_PERMISSION,
    TRANSCRIPT_CAN_MANAGE_ACADEMIC_RECORDS_PERMISSION,
    TRANSCRIPT_CAN_MANAGE_TRANSCRIPT_DOWNLOAD_PERMISSION,
  ];

  moduleSettings.forEach(name =>
    addModuleSettingIfAllowed({ permissions, context, name })
  );
  permissionNames.forEach(name =>
    addPermissionIfAllowed({ permissions, context, name })
  );

  return permissions;
};

const getSchoolPermissions = curriculumPrograms => {
  return _.reduce(
    curriculumPrograms,
    (result, program) => {
      const id = _.get(program, "id");
      if (!id) return result;

      result[id] = getCurriculumPermissions(id);
      return result;
    },
    {}
  );
};

const buildPermissionMap = curriculumPrograms => ({
  account: getAccountPermissions(),
  school: getSchoolPermissions(curriculumPrograms),
});

const TranscriptsPermissionProvider = ({
  children,
  curriculumPrograms,
  schoolId,
}) => {
  const { t } = useTranslation(["common"]);
  const permissionMap = useMemo(() => {
    const map = buildPermissionMap(curriculumPrograms);
    return map;
  }, [curriculumPrograms]);

  const hasAccess = schoolId
    ? _.get(permissionMap, ["school", schoolId], new Set()).has(
        TRANSCRIPT_FOR_PROGRAMME_LEVEL_VIEW_PERMISSION
      )
    : _.get(permissionMap, "account", new Set()).has(
        TRANSCRIPT_FOR_ACCOUNT_LEVEL_VIEW_PERMISSION
      );

  if (!hasAccess) {
    return (
      <div className={classes.noAccessContainer}>
        <EmptyState
          illustration={NoAccessIllustration}
          title={t("common:sorry_you_dont_have_access_to_this_page")}
          subtitle={t("common:please_get_in_touch_with_your_school_admin")}
        />
      </div>
    );
  }

  return (
    <TranscriptsPermissionContext.Provider value={permissionMap}>
      {children}
    </TranscriptsPermissionContext.Provider>
  );
};

TranscriptsPermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  curriculumPrograms: PropTypes.arrayOf(PropTypes.object),
  schoolId: PropTypes.string,
};

TranscriptsPermissionProvider.defaultProps = {
  curriculumPrograms: [],
  schoolId: null,
};

export const useTranscriptsPermissions = () => {
  const context = useContext(TranscriptsPermissionContext);

  if (_.isNil(context)) {
    throw new Error(
      "useTranscriptsPermissions must be used within a TranscriptsPermissionProvider"
    );
  }

  return context;
};

export default memo(TranscriptsPermissionProvider);
